import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { isValidEmail } from "@/utils/validation";
import { sanitizeText } from "@/lib/utils";

const MESSAGE_MAX_LENGTH = 5000;

interface FormState {
  name: string;
  company: string;
  email: string;
  message: string;
  website: string;
}

interface FieldErrors {
  name?: string;
  company?: string;
  email?: string;
  message?: string;
}

function validateForm(values: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = "Nome é obrigatório.";
  if (!values.company.trim()) errors.company = "Empresa é obrigatória.";
  if (!values.email.trim()) {
    errors.email = "E-mail é obrigatório.";
  } else if (!isValidEmail(values.email.trim())) {
    errors.email = "Informe um e-mail válido.";
  }
  if (!values.message.trim()) errors.message = "Sugestão é obrigatória.";
  return errors;
}

export default function FeedbackPage() {
  const [form, setForm] = useState<FormState>({ name: "", company: "", email: "", message: "", website: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [serverError, setServerError] = useState<string>("");

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FieldErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const fieldErrors = validateForm(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setStatus("loading");
    setServerError("");

    try {
      const base = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await fetch(`${base}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          message: sanitizeText(form.message.trim(), MESSAGE_MAX_LENGTH),
          website: form.website,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? "Erro ao enviar. Tente novamente.");
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(err instanceof Error ? err.message : "Erro ao enviar. Tente novamente.");
    }
  }

  return (
    <div className="min-h-screen bg-[#f5f6fa] flex flex-col items-center justify-center p-4">

      <div className="w-full max-w-md">

        <div className="flex justify-center mb-8">
          <img
            src="/convenia-logo.webp"
            alt="Convenia"
            className="h-10 w-auto object-contain"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          <div className="px-8 pt-7 pb-5 border-b border-gray-100">
            <h1 className="text-xl font-bold text-[#0d1a5e]">
              Feedback &amp; Sugestões
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Compartilhe sua opinião e nos ajude a melhorar o produto.
            </p>
          </div>

          <div className="px-8 py-7">
            {status === "success" ? (
              <div className="flex flex-col items-center py-6 gap-4 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-[#8dc63f]/[0.13]">
                  <CheckCircle2 className="w-8 h-8 text-[#8dc63f]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0d1a5e]">
                  Obrigado pelo feedback!
                </h3>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
                  Sua mensagem foi enviada com sucesso. Agradecemos sua contribuição.
                </p>
                <button
                  type="button"
                  className="mt-1 text-sm font-medium underline underline-offset-2 text-[#0d1a5e]"
                  onClick={() => {
                    setForm({ name: "", company: "", email: "", message: "", website: "" });
                    setStatus("idle");
                  }}
                >
                  Enviar outro feedback
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5">

                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium text-[#0d1a5e]">Nome</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Seu nome"
                    value={form.name}
                    onChange={handleChange}
                    disabled={status === "loading"}
                    className={`h-10 rounded-lg border-gray-200 focus-visible:ring-0 focus-visible:border-[#0d1a5e] ${errors.name ? "border-red-400 focus-visible:border-red-400" : ""}`}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="company" className="text-sm font-medium text-[#0d1a5e]">Empresa</Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder="Nome da empresa"
                    value={form.company}
                    onChange={handleChange}
                    disabled={status === "loading"}
                    className={`h-10 rounded-lg border-gray-200 ${errors.company ? "border-red-400" : ""}`}
                  />
                  {errors.company && <p className="text-xs text-red-500">{errors.company}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-medium text-[#0d1a5e]">E-mail</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={form.email}
                    onChange={handleChange}
                    disabled={status === "loading"}
                    className={`h-10 rounded-lg border-gray-200 ${errors.email ? "border-red-400" : ""}`}
                  />
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-sm font-medium text-[#0d1a5e]">Sugestões</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Descreva sua sugestão ou feedback..."
                    value={form.message}
                    onChange={handleChange}
                    disabled={status === "loading"}
                    rows={5}
                    className={`rounded-lg border-gray-200 resize-none ${errors.message ? "border-red-400" : ""}`}
                  />
                  {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                </div>

                {status === "error" && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{serverError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg font-semibold text-white transition-opacity hover:opacity-90 bg-[#0d1a5e] hover:bg-[#0d1a5e]"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar feedback"
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-5">
          Convenia · feedback
        </p>
      </div>
    </div>
  );
}
